import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@heroui/react';
import { ImageCropper, type ImageCropperRef } from '../../../../../packages/ui/src/components/extra/image-cropper';
import { IconRotate2 as RotateCcwSquare, IconRotate as RotateCwSquare } from '@tabler/icons-react';

export interface AvatarEditorProps {
  imageSrc: string;
  onCancel: () => void;
  onComplete: (imageFile: File) => void;
}

/**
 * 全屏头像编辑组件
 * 
 * 使用项目自有的 ImageCropper 组件，头像裁切模式
 * - 圆形裁切区域
 * - 1:1 宽高比
 * - 支持旋转、缩放
 * - 全屏显示
 * - 自定义操作区样式
 */
export function AvatarEditor({
  imageSrc,
  onCancel,
  onComplete,
}: AvatarEditorProps) {
  const cropperRef = useRef<ImageCropperRef>(null);

  // 逆时针旋转90度
  const handleRotateCCW = () => {
    cropperRef.current?.rotateCCW();
  };

  // 顺时针旋转90度
  const handleRotateCW = () => {
    cropperRef.current?.rotateCW();
  };

  // 完成编辑
  const handleComplete = async () => {
    try {
      await cropperRef.current?.complete();
    } catch (error) {
      console.error('头像编辑失败:', error);
      alert(error instanceof Error ? error.message : '头像编辑失败');
    }
  };

  const editorContent = (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col touch-none">
      {/* 裁切区域：touch-none 阻止浏览器将双指当作页面缩放，由 ImageCropper 独占处理 */}
      <div className="flex-1 min-h-0">
        <ImageCropper
          ref={cropperRef}
          image={imageSrc}
          mode="avatar"
          onComplete={onComplete}
          className="h-full w-full"
          cropPadding={20}
        />
      </div>

      {/* 下方：操作区 */}
      <div className="flex items-center justify-between px-4 py-4 bg-black/80 flex-shrink-0">
        {/* 左侧：旋转按钮 */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleRotateCCW}
            className="p-2 text-white hover:bg-white/20 rounded-md transition-colors"
            aria-label="逆时针旋转90度"
          >
            <RotateCcwSquare className="h-6 w-6" stroke={1.5} />
          </button>
          <button
            type="button"
            onClick={handleRotateCW}
            className="p-2 text-white hover:bg-white/20 rounded-md transition-colors"
            aria-label="顺时针旋转90度"
          >
            <RotateCwSquare className="h-6 w-6" stroke={1.5} />
          </button>
        </div>

        {/* 右侧：取消/完成按钮 */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/20"
            onPress={onCancel}
          >
            取消
          </Button>
          <Button
            variant="primary"
            className="bg-white text-black hover:bg-white/90"
            onPress={handleComplete}
          >
            完成
          </Button>
        </div>
      </div>
    </div>
  );

  // 使用 Portal 渲染到 document.body，确保脱离层叠上下文，覆盖 NavBar 和 TabBar
  return typeof document !== 'undefined' 
    ? createPortal(editorContent, document.body)
    : editorContent;
}
